import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
} from 'react';
import {useLocation} from './useLocation';
import {getPageType} from './getCurrentRouteFromUrl';
import {usePageTypePathsMap} from './RouterContext';
import {PageTypes} from './types';
import {useHistory} from './useHistory';

type PageTypeCache = Map<string, PageTypes | undefined>;

const PageTypeContext = createContext<PageTypes | undefined>(undefined);

export function PageTypeContextProvider({children}: PropsWithChildren) {
  const pageTypePerUrlCache = useRef<PageTypeCache>(new Map());
  const {pathname} = useLocation();
  const pageTypePathsMap = usePageTypePathsMap();

  const currentPageType =
    pageTypePerUrlCache.current.get(pathname) ??
    getPageType(pageTypePathsMap, pathname);
  if (!pageTypePerUrlCache.current.has(pathname)) {
    pageTypePerUrlCache.current.set(pathname, currentPageType);
  }

  return (
    <PageTypeContext.Provider value={currentPageType}>
      {children}
    </PageTypeContext.Provider>
  );
}

export const useCurrentPageTypeCallback = (): (() => PageTypes | undefined) => {
  const history = useHistory();
  const pageTypePathsMap = usePageTypePathsMap();

  return useCallback(
    () => getPageType(pageTypePathsMap, history.location.pathname),
    [history.location.pathname, pageTypePathsMap],
  );
};

export const useCurrentPageType = (): PageTypes | undefined => {
  return useContext(PageTypeContext);
};
